import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { v1Notices } from '../../../lib/v1';
import { type NoticeRow } from './types';
import { fmtDt } from './helpers';
import { WidgetCard } from './WidgetCard';
import { isPinned, useCanPinNotice } from '../../../lib/noticePin';

/* ─── NotizenWidget (compact) ─────────────────────────────────────────────── */

export function NotizenWidget({ dark, userId, onShowAll, onEditNotice, reloadRef, isDead = false }: { dark: boolean; userId: number; onShowAll: (e: React.MouseEvent) => void; onEditNotice?: (notice: NoticeRow) => void; reloadRef?: React.MutableRefObject<(() => void) | null>; /** PB verstorben → nur Administrator darf (ent)pinnen. */ isDead?: boolean }) {
  const { t } = useTranslation();
  const canPin = useCanPinNotice(isDead);
  const tc = getThemeColors(dark);
  const [notices, setNotices] = useState<NoticeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [comment, setComment] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const reload = useCallback(() => {
    setLoading(true);
    v1Notices.listForUser(userId)
      .then(list => setNotices((list as NoticeRow[]).slice(0, 5)))
      .catch(() => {/* ignore */})
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (reloadRef) reloadRef.current = reload;
    return () => { if (reloadRef) reloadRef.current = null; };
  }, [reloadRef, reload]);

  const handlePin = useCallback(async (id: number) => {
    try {
      const list = await v1Notices.pin(id);
      setNotices((list as NoticeRow[]).slice(0, 5));
    } catch { reload(); }
  }, [reload]);

  const handleAdd = useCallback(async (e: React.FormEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!comment.trim()) { setFormError(t('pflege.detail.dashboard.notice_required')); return; }
    setSaving(true); setFormError(null);
    try {
      const created = await v1Notices.create({ user_id: userId, comment: comment.trim() });
      const noticeRow = created as NoticeRow;
      setNotices(prev => [noticeRow, ...prev].slice(0, 5));
      setComment(''); setShowForm(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('pflege.detail.dashboard.save_error'));
    } finally { setSaving(false); }
  }, [userId, comment, t, reload]);

  return (
    <WidgetCard dark={dark} title={t('pflege.detail.dashboard.notizen')} badge={notices.length || null} onShowAll={onShowAll} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')} loading={loading}>
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {!loading && notices.length === 0 && !showForm && (
          <div style={{ padding: '10px 14px', fontSize: 12, color: tc.muted, fontStyle: 'italic' }}>{t('pflege.tasks.notes_empty')}</div>
        )}
        {notices.map(n => (
          <div
            key={n.id}
            role="button"
            tabIndex={0}
            aria-label={`Notiz: ${n.comment ?? n.nachricht ?? n.text ?? ''}`}
            onClick={(e) => { e.stopPropagation(); onEditNotice?.(n); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onEditNotice?.(n); } }}
            data-pinned={isPinned(n) ? 'true' : 'false'}
            style={{
              padding: '8px 14px', borderBottom: `1px solid ${tc.borderSoft}`, cursor: onEditNotice ? 'pointer' : 'default',
              background: isPinned(n) ? (dark ? 'rgba(181,41,122,0.12)' : BRAND_LIGHT) : undefined,
              borderLeft: isPinned(n) ? `3px solid ${BRAND}` : '3px solid transparent',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, gap: 6 }}>
              <div style={{ fontSize: 10.5, color: tc.muted }}>
                {isPinned(n) && <span style={{ color: BRAND, fontWeight: 700, marginRight: 6 }}>📌 {t('pflege.tasks.notice_pinned')}</span>}
                {fmtDt(n.created_at)}
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {canPin(n) && (
                  <button
                    type="button"
                    title={t(isPinned(n) ? 'pflege.tasks.notice_unpin' : 'pflege.tasks.notice_pin')}
                    aria-label={t(isPinned(n) ? 'pflege.tasks.notice_unpin' : 'pflege.tasks.notice_pin')}
                    onClick={(e) => { e.stopPropagation(); void handlePin(n.id); }}
                    onKeyDown={(e) => e.stopPropagation()}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, opacity: isPinned(n) ? 1 : 0.5 }}
                  >📌</button>
                )}
                {onEditNotice && <span style={{ fontSize: 10, color: tc.muted }}>✏</span>}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: tc.text, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {n.comment ?? n.nachricht ?? n.text ?? '—'}
            </div>
          </div>
        ))}
      </div>
      {showForm ? (
        <form onSubmit={handleAdd} onClick={(e) => e.stopPropagation()} style={{ borderTop: `1px solid ${tc.border}`, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea ref={textareaRef} rows={2} placeholder={t('pflege.detail.dashboard.notice_placeholder')} value={comment} onChange={e => setComment(e.target.value)} onClick={e => e.stopPropagation()} style={{ fontSize: 12.5, padding: '6px 10px', resize: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, background: tc.inputBg, color: tc.text, outline: 'none', fontFamily: 'inherit' }} />
          {formError && <div style={{ fontSize: 11, color: '#b42318' }}>{formError}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="submit" disabled={saving} style={{ flex: 1, fontSize: 12, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 7, padding: '6px 0', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? '…' : t('pflege.detail.dashboard.hinzufuegen')}</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); setShowForm(false); setComment(''); setFormError(null); }} style={{ fontSize: 12, padding: '6px 10px', background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, color: tc.muted, cursor: 'pointer' }}>{t('common.action.cancel')}</button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={(e) => { e.stopPropagation(); setShowForm(true); setTimeout(() => textareaRef.current?.focus(), 50); }} style={{ margin: '8px 14px 10px', fontSize: 12, fontWeight: 600, color: BRAND, background: BRAND_LIGHT, border: `1px dashed ${BRAND}`, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', textAlign: 'left' }}>+ {t('pflege.detail.dashboard.notiz_hinzufuegen')}</button>
      )}
    </WidgetCard>
  );
}

/* ─── Inline NoticeEditForm im Drawer ────────────────────────────────────── */

export interface NoticeEditFormProps {
  dark: boolean;
  notice: NoticeRow;
  onSaved: (updated: NoticeRow) => void;
  onCancel: () => void;
}

export function NoticeEditForm({ dark, notice, onSaved, onCancel }: NoticeEditFormProps) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();
  const [comment, setComment] = useState(notice.comment ?? notice.nachricht ?? notice.text ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) { setError(t('pflege.detail.dashboard.notice_required')); return; }
    setSaving(true); setError(null);
    try {
      const updated = await v1Notices.update(notice.id, { comment: comment.trim() });
      onSaved(updated as NoticeRow);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pflege.detail.dashboard.save_error'));
    } finally { setSaving(false); }
  }, [comment, notice.id, t, onSaved]);

  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: tc.muted, display: 'block', marginBottom: 5,
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <label style={labelStyle}>{t('pflege.detail.dashboard.notice_placeholder')}</label>
        <textarea
          rows={5}
          value={comment}
          onChange={e => setComment(e.target.value)}
          style={{
            fontSize: 13, padding: '8px 12px', width: '100%', boxSizing: 'border-box',
            border: `1px solid ${tc.border}`, borderRadius: 8,
            background: tc.inputBg, color: tc.text, outline: 'none',
            fontFamily: 'Outfit,sans-serif', resize: 'vertical',
          }}
        />
      </div>
      {error && (
        <div style={{ padding: '8px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 7, fontSize: 12.5, color: '#b42318' }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="submit" disabled={saving} style={{ flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'Outfit,sans-serif' }}>
          {saving ? '…' : t('common.action.save')}
        </button>
        <button type="button" onClick={onCancel} style={{ fontSize: 13, padding: '10px 18px', background: 'none', border: `1px solid ${tc.border}`, borderRadius: 8, color: tc.muted, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
          {t('common.action.cancel')}
        </button>
      </div>
    </form>
  );
}
